import { IconLayer, TextLayer } from '@deck.gl/layers';
import type { LayerState, LayerFeature, FlightProps } from '@vitals/shared';
import type { RendererDef } from './registry.js';
import { createFlightTracker, type FlightFix } from './flightTracker.js';
import { flightLabelsVisible } from './flightLabels.js';

const flightProps = (f: LayerFeature): FlightProps => f.props as unknown as FlightProps;

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));
const lerp = (a: number, b: number, t: number): number => Math.round(a + (b - a) * t);

export type AircraftKind = 'helicopter' | 'large' | 'jet' | 'light';

type AircraftClassificationProps = Pick<FlightProps, 'isHelicopter' | 'category' | 'typeCode'>
  & Partial<Pick<FlightProps, 'airline' | 'flightNumber'>>;

const LARGE_AIRCRAFT_CATEGORIES = new Set(['A3', 'A4', 'A5']);
const BUSINESS_JET_PREFIXES = ['C5', 'C6', 'C7', 'E5', 'FA', 'GLF', 'HA4', 'LJ', 'PRM'];

/** Best-effort visual class from fields supplied by ADS-B. Unknowns stay generic. */
export function aircraftKind(props: AircraftClassificationProps): AircraftKind {
  if (props.isHelicopter) return 'helicopter';
  if (LARGE_AIRCRAFT_CATEGORIES.has(props.category.toUpperCase()) || (props.airline && props.flightNumber)) return 'large';
  const typeCode = props.typeCode.toUpperCase();
  if (props.category.toUpperCase() === 'A6' || BUSINESS_JET_PREFIXES.some((prefix) => typeCode.startsWith(prefix))) {
    return 'jet';
  }
  return 'light';
}

const UNKNOWN_ALTITUDE_COLOR: [number, number, number] = [148, 163, 184];
const ALTITUDE_STOPS: readonly [number, [number, number, number]][] = [
  [0, [255, 126, 76]],       // surface / pattern altitude: warm coral
  [5_000, [255, 193, 72]],   // low: amber
  [10_000, [116, 214, 147]], // terminal ceiling: green
  [20_000, [70, 203, 224]],  // mid-altitude: cyan
  [30_000, [104, 166, 255]], // cruise: blue
  [40_000, [190, 139, 255]], // high cruise: violet
  [45_000, [226, 198, 255]], // exceptional altitude: pale violet
];

/** Piecewise altitude ramp with distinct bands at operationally useful levels. */
export function altitudeColor(altitude: number | null): [number, number, number] {
  if (altitude === null || !Number.isFinite(altitude)) return UNKNOWN_ALTITUDE_COLOR;
  const clamped = Math.min(ALTITUDE_STOPS.at(-1)![0], Math.max(0, altitude));
  const upperIndex = ALTITUDE_STOPS.findIndex(([feet]) => feet >= clamped);
  if (upperIndex <= 0) return [...ALTITUDE_STOPS[0]![1]];
  const [lowFeet, lowColor] = ALTITUDE_STOPS[upperIndex - 1]!;
  const [highFeet, highColor] = ALTITUDE_STOPS[upperIndex]!;
  const t = clamp01((clamped - lowFeet) / (highFeet - lowFeet));
  return [
    lerp(lowColor[0], highColor[0], t),
    lerp(lowColor[1], highColor[1], t),
    lerp(lowColor[2], highColor[2], t),
  ];
}

/** Two revolutions per second, expressed in degrees for deck.gl's IconLayer. */
export function rotorAngle(nowSec: number, reduceMotion = false): number {
  return reduceMotion ? 0 : (nowSec * 720) % 360;
}

// ─── Glyphs ────────────────────────────────────────────────────────────────────
// White silhouette masks drawn once and cached. mask:true lets IconLayer tint
// them per aircraft via getColor. Bodies point north so heading rotation works;
// the helicopter rotor is a separate mask with its own animation angle.

const GLYPH_PX = 22; // rendered size on screen
const ATLAS = 64; // drawing resolution for crispness on hi-dpi

interface IconDef {
  id: string;
  url: string;
  width: number;
  height: number;
  anchorX: number;
  anchorY: number;
  mask: true;
}

function drawLargeAircraft(ctx: CanvasRenderingContext2D, s: number): void {
  const c = s / 2;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.moveTo(c, s * 0.06);
  ctx.lineTo(c + s * 0.06, s * 0.42);
  ctx.lineTo(c + s * 0.46, s * 0.6);
  ctx.lineTo(c + s * 0.46, s * 0.68);
  ctx.lineTo(c + s * 0.06, s * 0.56);
  ctx.lineTo(c + s * 0.05, s * 0.8);
  ctx.lineTo(c + s * 0.2, s * 0.9);
  ctx.lineTo(c + s * 0.2, s * 0.96);
  ctx.lineTo(c, s * 0.88);
  ctx.lineTo(c - s * 0.2, s * 0.96);
  ctx.lineTo(c - s * 0.2, s * 0.9);
  ctx.lineTo(c - s * 0.05, s * 0.8);
  ctx.lineTo(c - s * 0.06, s * 0.56);
  ctx.lineTo(c - s * 0.46, s * 0.68);
  ctx.lineTo(c - s * 0.46, s * 0.6);
  ctx.lineTo(c - s * 0.06, s * 0.42);
  ctx.closePath();
  ctx.fill();
}

function drawJet(ctx: CanvasRenderingContext2D, s: number): void {
  const c = s / 2;
  ctx.fillStyle = '#fff';
  ctx.beginPath();
  ctx.moveTo(c, s * 0.08);
  ctx.lineTo(c + s * 0.055, s * 0.46);
  ctx.lineTo(c + s * 0.34, s * 0.72);
  ctx.lineTo(c + s * 0.31, s * 0.79);
  ctx.lineTo(c + s * 0.055, s * 0.63);
  ctx.lineTo(c + s * 0.16, s * 0.91);
  ctx.lineTo(c, s * 0.83);
  ctx.lineTo(c - s * 0.16, s * 0.91);
  ctx.lineTo(c - s * 0.055, s * 0.63);
  ctx.lineTo(c - s * 0.31, s * 0.79);
  ctx.lineTo(c - s * 0.34, s * 0.72);
  ctx.lineTo(c - s * 0.055, s * 0.46);
  ctx.closePath();
  ctx.fill();
}

function drawLightPlane(ctx: CanvasRenderingContext2D, s: number): void {
  const c = s / 2;
  ctx.fillStyle = '#fff';
  ctx.fillRect(c - s * 0.045, s * 0.14, s * 0.09, s * 0.7);
  ctx.fillRect(s * 0.12, s * 0.42, s * 0.76, s * 0.105);
  ctx.fillRect(s * 0.34, s * 0.76, s * 0.32, s * 0.075);
  ctx.beginPath();
  ctx.arc(c, s * 0.14, s * 0.045, 0, Math.PI * 2);
  ctx.fill();
}

function drawHeliBody(ctx: CanvasRenderingContext2D, s: number): void {
  const c = s / 2;
  ctx.fillStyle = '#fff';
  // tail boom (aft) + tail rotor
  ctx.fillRect(c - s * 0.03, c, s * 0.06, s * 0.4);
  ctx.fillRect(c - s * 0.1, s * 0.84, s * 0.2, s * 0.045);
  // body
  ctx.beginPath();
  ctx.ellipse(c, c - s * 0.04, s * 0.12, s * 0.2, 0, 0, Math.PI * 2);
  ctx.fill();
}

function drawRotor(ctx: CanvasRenderingContext2D, s: number): void {
  const c = s / 2;
  ctx.fillStyle = '#fff';
  ctx.save();
  ctx.translate(c, c - s * 0.04);
  ctx.rotate(0.42);
  ctx.fillRect(-s * 0.43, -s * 0.018, s * 0.86, s * 0.036);
  ctx.rotate(Math.PI / 2);
  ctx.fillRect(-s * 0.43, -s * 0.018, s * 0.86, s * 0.036);
  ctx.restore();
}

function makeGlyph(id: string, draw: (ctx: CanvasRenderingContext2D, s: number) => void): IconDef {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS;
  canvas.height = ATLAS;
  const ctx = canvas.getContext('2d')!;
  draw(ctx, ATLAS);
  return { id, url: canvas.toDataURL(), width: ATLAS, height: ATLAS, anchorX: ATLAS / 2, anchorY: ATLAS / 2, mask: true };
}

const glyphs = new Map<string, IconDef>();
function glyphFor(kind: AircraftKind): IconDef {
  const cached = glyphs.get(kind);
  if (cached) return cached;
  const draw = kind === 'helicopter' ? drawHeliBody
    : kind === 'large' ? drawLargeAircraft
      : kind === 'jet' ? drawJet
        : drawLightPlane;
  const glyph = makeGlyph(kind, draw);
  glyphs.set(kind, glyph);
  return glyph;
}

let rotorIcon: IconDef | null = null;
const getRotorIcon = (): IconDef => (rotorIcon ??= makeGlyph('helicopter-rotor', drawRotor));

interface FlightDatum {
  position: [number, number];
  angle: number;
  color: [number, number, number];
  kind: AircraftKind;
  props: FlightProps;
}

const tracker = createFlightTracker();
const reduceFlightMotion = typeof window === 'undefined' || typeof window.matchMedia !== 'function'
  ? null
  : window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Frame-driven flights layer: dead-reckons each aircraft forward from its last
 * fix and renders a heading-rotated glyph selected from the best available
 * metadata. Every kind shares the altitude ramp; helicopter rotors animate in a
 * separate overlay. Returns null when state is absent, down, or empty.
 */
export function buildFlightsLayer(state: LayerState, nowSec: number): IconLayer<FlightDatum> | (IconLayer<FlightDatum> | TextLayer<FlightDatum>)[] | null {
  if (!state || state.status === 'down' || state.features.length === 0) return null;

  const nowMs = typeof performance !== 'undefined' ? performance.now() : 0;
  const fixes: FlightFix[] = state.features.map((f) => {
    const p = flightProps(f);
    return {
      id: f.id,
      position: (f.geometry as { coordinates: [number, number] }).coordinates,
      headingDeg: p.heading,
      gsKt: p.groundSpeed,
    };
  });
  const samples = tracker.step(fixes, state.generatedAt, nowMs);

  const data: FlightDatum[] = state.features.map((f) => {
    const p = flightProps(f);
    const s = samples.get(f.id)!;
    return {
      position: s.position,
      // IconLayer getAngle is counter-clockwise degrees; compass heading is
      // clockwise from north, so negate to point the north-up glyph at heading.
      angle: -s.headingDeg,
      color: altitudeColor(p.altitude),
      kind: aircraftKind(p),
      props: p,
    };
  });

  const icons = new IconLayer<FlightDatum>({
    id: 'flights',
    data,
    getIcon: (d) => glyphFor(d.kind),
    getPosition: (d) => d.position,
    getAngle: (d) => d.angle,
    getColor: (d) => d.color,
    getSize: GLYPH_PX,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: true,
  });

  const helicopters = data.filter((d) => d.kind === 'helicopter');
  const rotors = helicopters.length === 0 ? null : new IconLayer<FlightDatum>({
    id: 'flight-helicopter-rotors',
    data: helicopters,
    getIcon: getRotorIcon,
    getPosition: (d) => d.position,
    getAngle: rotorAngle(nowSec, reduceFlightMotion?.matches ?? false),
    getColor: (d) => d.color,
    getSize: GLYPH_PX,
    sizeUnits: 'pixels',
    billboard: true,
    pickable: false,
  });

  if (!flightLabelsVisible()) return rotors ? [icons, rotors] : icons;

  const labels = new TextLayer<FlightDatum>({
    id: 'flights-labels',
    data,
    getPosition: (d) => d.position,
    getText: (d) => d.props.flightNumber || d.props.callsign,
    getColor: [232, 238, 252, 230],
    getSize: 11,
    sizeUnits: 'pixels',
    getPixelOffset: [0, -GLYPH_PX],
    getTextAnchor: 'middle',
    getAlignmentBaseline: 'bottom',
    fontFamily: '-apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif',
    fontWeight: 600,
    outlineWidth: 2,
    outlineColor: [6, 10, 18, 220],
    fontSettings: { sdf: true },
    background: false,
    pickable: false,
  });
  return rotors ? [icons, rotors, labels] : [icons, labels];
}

export const flightsRenderer: RendererDef = {
  layerId: 'flights',
  build: (state, nowSec) => buildFlightsLayer(state, nowSec),
};
